import React, { useCallback, useEffect, useRef, useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { ISelectedProtocol, ProtocolFrequencyType } from "../../../../services/types/utils.types";
import styles from "./CreateAppointmentModal.module.css";

interface IProtocolOption {
  id: number;
  desc: string;
}

interface IProtocolsManagerProps {
  repeatCount: number;
  protocols: IProtocolOption[];
  selectedProtocols: ISelectedProtocol[];
  onChange: (protocols: ISelectedProtocol[]) => void;
  onValidationChange: (isValid: boolean) => void;
}

const AFTER_FIRST_TYPES: ProtocolFrequencyType[] = ["afterFirst", "afterFirstOnce"];

function validateProtocolRow(
  protocol: ISelectedProtocol,
  repeatCount: number
): { isValid: boolean; errorMessage: string } {
  if (AFTER_FIRST_TYPES.includes(protocol.frequencyType) && repeatCount < 2) {
    return {
      isValid: false,
      errorMessage: t("PROTOCOL_AFTER_FIRST_MIN_SESSIONS"),
    };
  }

  if (protocol.frequencyType !== "sequential") {
    return { isValid: true, errorMessage: "" };
  }

  const val = protocol.everyNSessions;
  if (!val || val < 1 || val > repeatCount) {
    return {
      isValid: false,
      errorMessage: t("PROTOCOL_SEQ_VALIDATION_ERROR", { count: repeatCount }),
    };
  }

  return { isValid: true, errorMessage: "" };
}

function ProtocolsManager({
  repeatCount,
  protocols,
  selectedProtocols,
  onChange,
  onValidationChange,
}: IProtocolsManagerProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Reset "after first" options when session count drops to 1
  useEffect(() => {
    if (repeatCount >= 2) return;

    const needsReset = selectedProtocols.some((p) => AFTER_FIRST_TYPES.includes(p.frequencyType));
    if (needsReset) {
      onChange(
        selectedProtocols.map((p) =>
          AFTER_FIRST_TYPES.includes(p.frequencyType)
            ? { ...p, frequencyType: "every" as ProtocolFrequencyType }
            : p
        )
      );
    }
  }, [repeatCount, selectedProtocols, onChange]);

  const validateAll = useCallback(() => {
    if (selectedProtocols.length === 0) {
      onValidationChange(true);
      return;
    }

    const allValid = selectedProtocols.every(
      (p) => validateProtocolRow(p, repeatCount).isValid
    );
    onValidationChange(allValid);
  }, [selectedProtocols, repeatCount, onValidationChange]);

  useEffect(() => {
    validateAll();
  }, [validateAll]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleProtocol = useCallback(
    (protocol: IProtocolOption, checked: boolean) => {
      if (checked) {
        onChange([
          ...selectedProtocols,
          {
            protocolId: protocol.id,
            name: protocol.desc,
            frequencyType: "every",
            everyNSessions: Math.min(3, repeatCount),
          },
        ]);
      } else {
        onChange(selectedProtocols.filter((p) => p.protocolId !== protocol.id));
      }
    },
    [selectedProtocols, onChange, repeatCount]
  );

  const handleRemove = useCallback(
    (protocolId: number) => {
      onChange(selectedProtocols.filter((p) => p.protocolId !== protocolId));
    },
    [selectedProtocols, onChange]
  );

  const handleFrequencyChange = useCallback(
    (protocolId: number, frequencyType: ProtocolFrequencyType) => {
      onChange(
        selectedProtocols.map((p) =>
          p.protocolId === protocolId ? { ...p, frequencyType } : p
        )
      );
    },
    [selectedProtocols, onChange]
  );

  const handleEveryNChange = useCallback(
    (protocolId: number, value: number) => {
      onChange(
        selectedProtocols.map((p) =>
          p.protocolId === protocolId ? { ...p, everyNSessions: value } : p
        )
      );
    },
    [selectedProtocols, onChange]
  );

  const selectedIds = new Set(selectedProtocols.map((p) => p.protocolId));
  const canUseAfterFirst = repeatCount >= 2;

  return (
    <div className={styles.planCard}>
      <div className={styles.planCardHeader}>
        <span>{t("PROTOCOLS") || "פרוטוקולים"}</span>
      </div>

      <div className={styles.protocolSelectWrap} ref={wrapRef}>
        <button
          type="button"
          className={styles.protocolAddBtn}
          onClick={() => setIsMenuOpen((prev) => !prev)}
        >
          + {t("ADD_PROTOCOL") || "הוספת פרוטוקול"}
        </button>
        <div className={`${styles.protocolMenu} ${isMenuOpen ? styles.protocolMenuOpen : ""}`}>
          {protocols.length === 0 ? (
            <div className={styles.protocolMenuItem}>{t("NO_RESULTS")}</div>
          ) : (
            protocols.map((protocol) => (
              <label key={protocol.id} className={styles.protocolMenuItem}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(protocol.id)}
                  onChange={(e) => handleToggleProtocol(protocol, e.target.checked)}
                />
                {protocol.desc}
              </label>
            ))
          )}
        </div>
      </div>

      <div className={styles.protocolList}>
        {selectedProtocols.length === 0 ? (
          <div className={styles.emptyProtocols}>
            {t("NO_PROTOCOLS_SELECTED") || "טרם נבחרו פרוטוקולים"}
          </div>
        ) : (
          selectedProtocols.map((protocol) => {
            const rowId = `protocol-${protocol.protocolId}`;
            const { isValid, errorMessage } = validateProtocolRow(protocol, repeatCount);
            const isSequential = protocol.frequencyType === "sequential";
            const showError = !isValid && errorMessage;

            return (
              <div key={protocol.protocolId} className={styles.protocolItem}>
                <span className={styles.protocolName}>{protocol.name}</span>
                <div className={styles.freqOptions}>
                  <label className={styles.freqOption}>
                    <input
                      type="radio"
                      name={rowId}
                      value="every"
                      checked={protocol.frequencyType === "every"}
                      onChange={() => handleFrequencyChange(protocol.protocolId, "every")}
                    />
                    {t("PROTOCOL_AFTER_EVERY_TREATMENT") || "אחרי כל טיפול"}
                  </label>
                  <label className={styles.freqOption}>
                    <input
                      type="radio"
                      name={rowId}
                      value="sequential"
                      checked={protocol.frequencyType === "sequential"}
                      onChange={() =>
                        handleFrequencyChange(protocol.protocolId, "sequential")
                      }
                    />
                    {t("PROTOCOL_SEQUENTIAL") || "סדרתי"}
                  </label>
                  <div
                    className={`${styles.seqInput} ${!isSequential ? styles.seqInputDisabled : ""}`}
                  >
                    {t("PROTOCOL_ONCE_EVERY") || "אחת ל-"}
                    <input
                      type="number"
                      min={1}
                      className={`${styles.seqNumberInput} ${
                        isSequential && !isValid ? styles.seqNumberInputInvalid : ""
                      }`}
                      value={protocol.everyNSessions}
                      disabled={!isSequential}
                      onChange={(e) =>
                        handleEveryNChange(protocol.protocolId, parseInt(e.target.value, 10) || 0)
                      }
                    />
                    {t("PROTOCOL_SESSIONS") || "מפגשים"}
                  </div>
                  <label
                    className={`${styles.freqOption} ${!canUseAfterFirst ? styles.freqOptionDisabled : ""}`}
                    title={!canUseAfterFirst ? t("PROTOCOL_AFTER_FIRST_MIN_SESSIONS") : undefined}
                  >
                    <input
                      type="radio"
                      name={rowId}
                      value="afterFirst"
                      checked={protocol.frequencyType === "afterFirst"}
                      disabled={!canUseAfterFirst}
                      onChange={() =>
                        handleFrequencyChange(protocol.protocolId, "afterFirst")
                      }
                    />
                    {t("PROTOCOL_AFTER_FIRST_TREATMENT") || "אחרי טיפול ראשון — בכל המפגשים"}
                  </label>
                  <label
                    className={`${styles.freqOption} ${!canUseAfterFirst ? styles.freqOptionDisabled : ""}`}
                    title={!canUseAfterFirst ? t("PROTOCOL_AFTER_FIRST_MIN_SESSIONS") : undefined}
                  >
                    <input
                      type="radio"
                      name={rowId}
                      value="afterFirstOnce"
                      checked={protocol.frequencyType === "afterFirstOnce"}
                      disabled={!canUseAfterFirst}
                      onChange={() =>
                        handleFrequencyChange(protocol.protocolId, "afterFirstOnce")
                      }
                    />
                    {t("PROTOCOL_AFTER_FIRST_ONCE") || "אחרי טיפול ראשון — פעם אחת"}
                  </label>
                </div>
                <button
                  type="button"
                  className={styles.protocolRemove}
                  onClick={() => handleRemove(protocol.protocolId)}
                  aria-label={t("REMOVE") || "הסר"}
                >
                  ✕
                </button>
                {showError && (
                  <div className={`${styles.seqError} ${styles.seqErrorShow}`}>
                    {errorMessage}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ProtocolsManager;
